import { type CurrentOfferConfigData } from "../../entities/CurrentOfferConfig";

/**
 * TRE-2388 — where the stable links `/aktualna-oferta` (new clients) and
 * `/aktualna-oferta-przedluzenie` (current clients) lead. Full rules, the fallbacks and why the
 * redirect is never a 301: docs/aktualna-oferta.md.
 *
 * The Sales Manager picks one variant per link by hand (`app_config` current_offer). The pick is
 * checked again on every visit, so a promotion that ended, was switched off or turned into a
 * bank-transfer one stops being linked on its own.
 */

/** Who the link is for: new clients buy on `/step`, current ones renew on `/przedluzenie`. */
export type CurrentOfferAudience = "new" | "renewal";

/** The evergreen page every doubt or error ends on, never a dead promotion. */
export const CURRENT_OFFER_FALLBACK_PATH = "/pakiety";

/** The longest a redirect may be cached; never longer than the promotion's own end. */
export const CURRENT_OFFER_MAX_CACHE_SECONDS = 300;

const CHECKOUT_PATH_PREFIX: Record<CurrentOfferAudience, string> = {
  new: "/step",
  renewal: "/przedluzenie",
};

/** The `checkout_variant` columns the rule reads. `activeUntil` is in unix SECONDS. */
export interface CurrentOfferVariant {
  id: string;
  slug: string;
  status: string;
  target: string;
  excluded: boolean | null;
  default: boolean | null;
  activeUntil: number | null;
  createdAt: string;
  metadata: { allowManualPaymentForPrices?: string[] } | null;
}

export type CurrentOfferIneligibility = "inactive" | "wrongTarget" | "excluded" | "manualPayment" | "expired";

export type CurrentOfferPickFailure = "notConfigured" | "variantMissing" | CurrentOfferIneligibility;

export type CurrentOfferResolution =
  | {
      type: "offer";
      path: string;
      slug: string;
      activeUntil: number | null;
      cacheSeconds: number;
      /** Set when the pick failed and the renewal link fell back to the default renewal variant. */
      pickFailure: CurrentOfferPickFailure | null;
    }
  | { type: "fallback"; reason: CurrentOfferPickFailure };

const MANUAL_SLUG_SUFFIX = "-manual";

/**
 * Bank-transfer variants never go to e-mails: the `-manual` slug, or a transfer for every price.
 * A transfer for some prices only is an option next to the card (e.g. `blackfriday2026`), so it stays.
 */
export const isManualPaymentVariant = (variant: Pick<CurrentOfferVariant, "slug" | "metadata">): boolean =>
  variant.slug.endsWith(MANUAL_SLUG_SUFFIX) || !!variant.metadata?.allowManualPaymentForPrices?.includes("all");

/** `/przedluzenie` renders renewal variants only, so the renewal link takes nothing else. */
const isTargetFor = (target: string, audience: CurrentOfferAudience) =>
  audience === "renewal" ? target === "renewal" : target === "new" || target === "all";

const toUnixSeconds = (now: Date) => Math.floor(now.getTime() / 1000);

/**
 * Why a variant may not be behind the link, or null when it may. A variant without an end date
 * is allowed: the Sales Manager picked it on purpose and changes it by hand.
 */
export const getCurrentOfferIneligibility = (
  variant: CurrentOfferVariant,
  audience: CurrentOfferAudience,
  now: Date,
): CurrentOfferIneligibility | null => {
  if (variant.status !== "active") {
    return "inactive";
  }
  if (!isTargetFor(variant.target, audience)) {
    return "wrongTarget";
  }
  if (variant.excluded) {
    return "excluded";
  }
  if (isManualPaymentVariant(variant)) {
    return "manualPayment";
  }
  // The checkout treats a variant as ended once `activeUntil` is in the past, so the last second still sells.
  if (variant.activeUntil !== null && variant.activeUntil < toUnixSeconds(now)) {
    return "expired";
  }
  return null;
};

/** The variant id picked for the link, or undefined when nothing is picked. */
export const getPickedVariantId = (config: CurrentOfferConfigData | null, audience: CurrentOfferAudience) =>
  (audience === "renewal" ? config?.renewalVariantId : config?.variantId)?.trim() || undefined;

/**
 * PostgREST `or` filter for the only variants the rule reads: both picks and the active default
 * renewal variants. A product's whole variant list can pass the 1000-row cap, so never fetch it.
 */
export const currentOfferVariantsFilter = (config: CurrentOfferConfigData | null): string => {
  const defaultRenewals = "and(default.is.true,target.eq.renewal,status.eq.active)";
  const pickedIds = [config?.variantId, config?.renewalVariantId]
    .map((id) => id?.trim())
    .filter((id): id is string => !!id);

  return pickedIds.length
    ? `id.in.(${pickedIds.map((id) => `"${id}"`).join(",")}),${defaultRenewals}`
    : defaultRenewals;
};

/** The newest eligible renewal variant marked default in the admin („Wariant domyślny”). */
const findDefaultRenewalVariant = (variants: CurrentOfferVariant[], now: Date) =>
  variants
    .filter((variant) => variant.default && !getCurrentOfferIneligibility(variant, "renewal", now))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

const toOffer = (
  variant: CurrentOfferVariant,
  audience: CurrentOfferAudience,
  now: Date,
  pickFailure: CurrentOfferPickFailure | null,
): CurrentOfferResolution => {
  const secondsLeft =
    variant.activeUntil === null ? CURRENT_OFFER_MAX_CACHE_SECONDS : variant.activeUntil - toUnixSeconds(now) + 1;

  return {
    type: "offer",
    path: `${CHECKOUT_PATH_PREFIX[audience]}/${encodeURIComponent(variant.slug)}`,
    slug: variant.slug,
    activeUntil: variant.activeUntil,
    cacheSeconds: Math.max(0, Math.min(CURRENT_OFFER_MAX_CACHE_SECONDS, secondsLeft)),
    pickFailure,
  };
};

/**
 * @param variants every variant of the configured product (`config.productId`); a picked variant
 * missing from it was deleted or belongs elsewhere.
 */
export const resolveCurrentOffer = (
  config: CurrentOfferConfigData | null,
  variants: CurrentOfferVariant[],
  audience: CurrentOfferAudience,
  now: Date,
): CurrentOfferResolution => {
  if (!config?.productId?.trim()) {
    return { type: "fallback", reason: "notConfigured" };
  }

  const pickedId = getPickedVariantId(config, audience);
  const picked = pickedId ? variants.find((variant) => variant.id === pickedId) : undefined;
  const pickFailure: CurrentOfferPickFailure | null = !pickedId
    ? "notConfigured"
    : !picked
      ? "variantMissing"
      : getCurrentOfferIneligibility(picked, audience, now);

  if (picked && !pickFailure) {
    return toOffer(picked, audience, now, null);
  }

  const defaultRenewal = audience === "renewal" ? findDefaultRenewalVariant(variants, now) : undefined;
  if (defaultRenewal && pickFailure) {
    return toOffer(defaultRenewal, audience, now, pickFailure);
  }

  return { type: "fallback", reason: pickFailure ?? "notConfigured" };
};

/** The path the link redirects to, with the visitor's query string (UTMs, e-mail) as it came. */
export const buildCurrentOfferPath = (resolution: CurrentOfferResolution, search: string): string => {
  const path = resolution.type === "offer" ? resolution.path : CURRENT_OFFER_FALLBACK_PATH;
  const query = search.startsWith("?") ? search.slice(1) : search;

  return query ? `${path}?${query}` : path;
};
